/**
 * Module 110 (WP-110.1) — the balance changes the player has not been SHOWN yet.
 *
 * ## The bug this exists for
 *
 * `Bugs/Coin Increament_1.mp4` and `_2.mp4`: the Top Bar read the real balance,
 * and the real balance changes the instant `economy.credit()` runs — which is
 * always while something covers the pill (the win is credited on the frame the
 * board clears, before its popup; Double Coins and the Shop top-up behind their
 * popups' dim). When the cover lifted, nothing moved, so the reward never met
 * the total it went into.
 *
 * ## So there are two balances
 *
 * The REAL balance (economy store, server, saves, every price check) changes
 * exactly when it always did. This queue holds the transactions the pill has not
 * played yet, and the pill shows `real − heldDelta()`. Nothing that decides what
 * the player can afford may read that shown number: a queued SPEND makes it
 * larger than the real balance.
 *
 * Pure and DOM-free; the hook in `use-shown-balance.ts` decides WHEN to release.
 * Every caller is expected to record through a wallet wrapper rather than by
 * hand, so a new coin source or sink is queued without anyone remembering to.
 */
/** The signed change an event makes to its currency's balance. */
export function signedAmount(event) {
    return event.kind === 'credit' ? event.amount : -event.amount;
}
export class CoinMotionQueue {
    constructor() {
        this._pending = [];
        this._seq = 0;
        this._version = 0;
        this._listeners = new Set();
        /** For `useSyncExternalStore`. */
        this.subscribe = (listener) => {
            this._listeners.add(listener);
            return () => this._listeners.delete(listener);
        };
        /** Changes whenever `pending` does — the `useSyncExternalStore` snapshot. */
        this.getVersion = () => this._version;
    }
    /** Record a completed transaction. A zero or negative amount moved nothing and is ignored. */
    push(event) {
        if (!(event.amount > 0))
            return;
        this._pending = [...this._pending, { ...event, seq: ++this._seq }];
        this._changed();
    }
    /** Not yet shown, oldest first. */
    get pending() {
        return this._pending;
    }
    /** The signed sum of what `currency`'s pill has not shown yet. */
    heldDelta(currency) {
        let sum = 0;
        for (const e of this._pending)
            if (e.currency === currency)
                sum += signedAmount(e);
        return sum;
    }
    /** Take every pending event of `currency` — the caller is about to show them. */
    release(currency) {
        return this.releaseWhere((e) => e.currency === currency);
    }
    /** Take the pending events `pick` accepts, oldest first; the rest stay held. */
    releaseWhere(pick) {
        const taken = this._pending.filter(pick);
        if (taken.length === 0)
            return taken;
        this._pending = this._pending.filter((e) => !pick(e));
        this._changed();
        return taken;
    }
    /** Drop everything unshown: the pill then reads the real balance at once. */
    clear() {
        if (this._pending.length === 0)
            return;
        this._pending = [];
        this._changed();
    }
    _changed() {
        this._version += 1;
        for (const l of this._listeners)
            l();
    }
}
